import React, { useEffect, useMemo, useState } from "react";
import { SECTION_HEADING } from "../utils/homeType";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import HelpMark from "./HelpMark";
import { dayKey, hasPractised, loadPlay, msUntilNextGame } from "../games/daily";
import * as higherLower from "../games/higherLower";
import * as homeField from "../games/homeField";
import higherLowerArt from "../assets/games/higher-lower.jpg";
import homeFieldArt from "../assets/games/home-field.jpg";

// The two daily games, side by side on /home, between the leagues and This
// Week's games. Each is one picture card that opens its game, where the rules
// are.
//
// Both games are open every day. Once today's game is played the card greys
// out and says to come back tomorrow, and still opens: the result screen
// inside is where the score is shared.
//
// Playing takes an account, though not a league. In the demo the cards show
// "Sign up to play" and open sign up.
//
// The pictures (src/assets/games) are 6:5 and carry the title and a tagline.
// They have no arrow and no label: the "Daily Games" heading above already
// says what the cards are, and the app draws its own chevron in the corner.
// The app adds what changes: a small status pill at the bottom left ("New",
// "4 of 10 answered", the score once played, "Sign up to play"), and a grey
// wash once today's game is played.
// Reads only this device's record of play (games/daily.js), so it costs no
// query. Higher or Lower also needs a finished week, which it reads from the
// teams map AuthContext already holds.

// Matches the shape of the pictures, so cover shows all of each one.
const CARD_ASPECT = "6 / 5";

const GAMES = [
  { module: higherLower, path: "/games/higher-lower", art: higherLowerArt },
  { module: homeField, path: "/games/home-field", art: homeFieldArt },
];

function GameCard({ game, day, closed, demo }) {
  const navigate = useNavigate();
  const { module: { GAME_ID, GAME_NAME, QUESTIONS }, path, art } = game;
  const play = loadPlay(GAME_ID, day);
  const isNew = !hasPractised(GAME_ID) && play.answers.length === 0;
  const started = play.answers.length > 0 && !play.done;
  const score = play.answers.filter(Boolean).length;
  const dimmed = !demo && (play.done || Boolean(closed));

  // The pill says what the picture cannot. Nothing for a game not yet
  // started today that has been played before.
  let status = isNew ? "New" : null;
  if (demo) status = "Sign up to play";
  else if (closed) status = closed;
  else if (play.done) status = `${score}/${QUESTIONS}. Come back tomorrow.`;
  else if (started) status = `${play.answers.length} of ${QUESTIONS} answered`;
  const callout = demo || (isNew && !dimmed);

  return (
    <button
      type="button"
      onClick={() => navigate(demo ? "/signup" : path)}
      disabled={Boolean(closed)}
      aria-label={status ? `${GAME_NAME}. ${status}` : GAME_NAME}
      style={{
        position: "relative", display: "block", minWidth: 0, width: "100%", aspectRatio: CARD_ASPECT, padding: 0,
        textAlign: "left", borderRadius: 18, overflow: "hidden", border: "none", cursor: closed ? "default" : "pointer",
        backgroundColor: "#050d1f", WebkitTapHighlightColor: "transparent",
      }}
    >
      <img
        src={art}
        alt=""
        draggable={false}
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top",
          filter: dimmed ? "grayscale(1) brightness(0.5)" : "none",
        }}
      />
      {status && (
        <span
          style={{
            position: "absolute", left: 10, bottom: 12, maxWidth: "calc(100% - 64px)", padding: "7px 11px", borderRadius: 14,
            backgroundColor: "rgba(5,13,31,0.78)", border: "1px solid rgba(255,255,255,0.28)",
            fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: callout ? "#7dffa3" : "#fff",
          }}
        >
          {status}
        </span>
      )}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", right: 10, bottom: 10, width: 34, height: 34, borderRadius: "50%", display: "flex",
          alignItems: "center", justifyContent: "center", border: "1.5px solid rgba(255,255,255,0.7)",
          backgroundColor: "rgba(5,13,31,0.5)", opacity: dimmed ? 0.5 : 1,
        }}
      >
        <ChevronRight size={20} strokeWidth={2.4} color="#fff" />
      </span>
    </button>
  );
}

function GamesSection({ demo = false }) {
  const { teams, seasonConfig } = useAuth();
  const [day, setDay] = useState(() => dayKey());

  // A card left open across midnight Eastern rolls over to the new day.
  useEffect(() => {
    const id = setTimeout(() => setDay(dayKey()), msUntilNextGame() + 1000);
    return () => clearTimeout(id);
  }, [day]);

  const hlClosed = useMemo(
    () => (higherLower.gameWeek(teams, seasonConfig?.currentWeek) === null ? "Opens once the first week is final." : null),
    [teams, seasonConfig],
  );

  return (
    <div style={{ marginTop: 32 }} data-tour="home-daily">
      <h2 style={{ ...SECTION_HEADING, marginBottom: 10 }}>
        Daily Games
        <HelpMark step="home-daily" label="How the Daily Games work" demo={demo} />
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
        <GameCard game={GAMES[0]} day={day} closed={demo ? null : hlClosed} demo={demo} />
        <GameCard game={GAMES[1]} day={day} closed={null} demo={demo} />
      </div>
    </div>
  );
}

export default GamesSection;
